import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  MessageSquare, 
  Phone,
  Video,
  Send,
  Search,
  User,
  Building2,
  Settings
} from 'lucide-react';
import { demoMessaggi } from './demoData';

const DemoMessaggi = ({ userType }) => {
  const [messaggi] = useState(demoMessaggi[userType] || []);
  const [conversazioneAperta, setConversazioneAperta] = useState(null);
  const [nuovoMessaggio, setNuovoMessaggio] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const messaggiConversazione = {
    "msg-1": [
      { id: 1, testo: "Buongiorno, sono interessato alla posizione di agente immobiliare", timestamp: "10:15", mittente: "altro" },
      { id: 2, testo: "Buongiorno Marco! Grazie per il suo interesse. Ha già esperienza nel settore?", timestamp: "10:18", mittente: "io" },
      { id: 3, testo: "Sì, ho lavorato 3 anni in una piccola agenzia locale. Ora cerco nuove sfide!", timestamp: "10:30", mittente: "altro" }
    ],
    "msg-c1": [
      { id: 1, testo: "Ciao Mario! Ti abbiamo selezionato per un colloquio", timestamp: "14:15", mittente: "altro" },
      { id: 2, testo: "Wow, grazie mille! Sono molto interessato 😊", timestamp: "14:18", mittente: "io" },
      { id: 3, testo: "Perfetto! Ti chiameremo domani per fissare l'appuntamento", timestamp: "14:20", mittente: "altro" }
    ]
  };

  const handleInviaMessaggio = () => {
    if (nuovoMessaggio.trim()) {
      console.log(`Demo: Messaggio inviato: ${nuovoMessaggio}`);
      setNuovoMessaggio('');
    }
  };

  const getIconaContatto = (tipo) => {
    switch (tipo) {
      case 'candidato': return <User className="w-4 h-4" />;
      case 'agenzia': return <Building2 className="w-4 h-4" />;
      case 'sistema': return <Settings className="w-4 h-4" />;
      default: return <MessageSquare className="w-4 h-4" />;
    }
  };

  const filteredMessaggi = messaggi.filter(msg =>
    msg.contatto.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (conversazioneAperta) {
    const messaggio = messaggi.find(m => m.id === conversazioneAperta);
    const conversazione = messaggiConversazione[conversazioneAperta] || [];
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50 flex flex-col">
        {/* Header Conversazione */}
        <div className="bg-white border-b border-gray-200 shadow-sm p-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setConversazioneAperta(null)}
                variant="outline"
                size="sm"
                className="rounded-xl"
              >
                ← Indietro
              </Button>
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                {messaggio?.foto}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{messaggio?.contatto}</h3>
                <p className="text-sm text-green-600">Online</p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-xl">
                <Phone className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" className="rounded-xl">
                <Video className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Messaggi */}
        <div className="flex-1 max-w-4xl mx-auto w-full p-4 space-y-4">
          {conversazione.map((msg) => (
            <div key={msg.id} className={`flex ${msg.mittente === 'io' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl ${
                msg.mittente === 'io' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-900'
              }`}>
                <p className="text-sm">{msg.testo}</p>
                <p className={`text-xs mt-1 ${
                  msg.mittente === 'io' ? 'text-blue-100' : 'text-gray-500'
                }`}>
                  {msg.timestamp}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Input Messaggio */}
        <div className="bg-white border-t border-gray-200 p-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex gap-2">
              <input
                type="text"
                value={nuovoMessaggio}
                onChange={(e) => setNuovoMessaggio(e.target.value)}
                placeholder="Scrivi un messaggio..."
                className="flex-1 px-4 py-2 border rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && handleInviaMessaggio()}
              />
              <Button 
                onClick={handleInviaMessaggio}
                className="bg-blue-500 hover:bg-blue-600 text-white rounded-2xl px-4"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header e Ricerca */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Messaggi</h2>
        <Badge className="bg-blue-100 text-blue-800">
          {messaggi.reduce((acc, msg) => acc + msg.non_letti, 0)} non letti
        </Badge>
      </div>

      {/* Ricerca */}
      <div className="relative">
        <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Cerca conversazioni..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Lista Messaggi */}
      <div className="space-y-2">
        {filteredMessaggi.map((messaggio) => (
          <Card 
            key={messaggio.id} 
            className="hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => setConversazioneAperta(messaggio.id)}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                    {messaggio.foto}
                  </div>
                  {messaggio.non_letti > 0 && (
                    <Badge className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[20px] h-5 flex items-center justify-center">
                      {messaggio.non_letti}
                    </Badge>
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-gray-900 truncate">{messaggio.contatto}</h4>
                      {getIconaContatto(messaggio.tipo)}
                    </div>
                    <span className="text-xs text-gray-500">{messaggio.timestamp}</span>
                  </div>
                  <p className={`text-sm truncate ${
                    messaggio.non_letti > 0 ? 'font-semibold text-gray-900' : 'text-gray-600'
                  }`}>
                    {messaggio.ultima_msg}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Demo Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
        <div className="flex items-start gap-2">
          <MessageSquare className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-700">
            <strong>Demo Messaggi:</strong> Clicca su una conversazione per vedere l'esperienza completa. 
            In modalità demo, i messaggi sono simulati ma l'interfaccia è identica alla versione reale.
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoMessaggi;